export const BLACK_AND_WHITE_TILE_NUMBERS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9,
] as const;

export type TileNumber = (typeof BLACK_AND_WHITE_TILE_NUMBERS)[number];
export type TileColor = "WHITE" | "BLACK";
export type RoundPhase = "FIRST_SELECTION" | "SECOND_SELECTION";
export type GameStatus = "IN_PROGRESS" | "COMPLETED";
export type RoundOutcome = "FIRST_WIN" | "SECOND_WIN" | "DRAW";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface BlackAndWhitePlayer {
  readonly playerId: string;
  readonly score: number;
  readonly remainingTiles: readonly TileNumber[];
  readonly usedTiles: readonly TileNumber[];
}

export interface TileSubmission {
  readonly playerId: string;
  readonly tile: TileNumber;
  readonly color: TileColor;
}

export interface RoundInProgress {
  readonly roundNumber: number;
  readonly phase: RoundPhase;
  readonly firstPlayerId: string;
  readonly secondPlayerId: string;
  readonly firstSubmission: TileSubmission | null;
}

export interface CompletedRound {
  readonly roundNumber: number;
  readonly firstPlayerId: string;
  readonly secondPlayerId: string;
  readonly firstSubmission: TileSubmission;
  readonly secondSubmission: TileSubmission;
  readonly outcome: RoundOutcome;
  readonly winnerId: string | null;
}

export interface BlackAndWhiteState {
  readonly gameId: string;
  readonly hostPlayerId: string;
  readonly observerGodViewEnabled: boolean;
  readonly status: GameStatus;
  readonly players: readonly BlackAndWhitePlayer[];
  readonly completedRounds: readonly CompletedRound[];
  readonly currentRound: RoundInProgress | null;
  readonly winnerId: string | null;
  readonly maxRounds: number;
  readonly targetScore: number;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly playerIds: readonly string[];
  readonly hostPlayerId: string;
  readonly firstPlayerId?: string;
  readonly observerGodViewEnabled?: boolean;
}

export interface SubmitTileAction {
  readonly type: "SubmitTile";
  readonly playerId: string;
  readonly tile: TileNumber;
}

export type BlackAndWhiteAction = SubmitTileAction;

export interface ViewerSubmission {
  readonly playerId: string;
  readonly color: TileColor;
  readonly tile: TileNumber | null;
}

export interface ViewerRoundInProgress {
  readonly roundNumber: number;
  readonly phase: RoundPhase;
  readonly firstPlayerId: string;
  readonly secondPlayerId: string;
  readonly firstSubmission: ViewerSubmission | null;
}

export interface ViewerCompletedRound {
  readonly roundNumber: number;
  readonly firstPlayerId: string;
  readonly secondPlayerId: string;
  readonly firstSubmission: ViewerSubmission;
  readonly secondSubmission: ViewerSubmission;
  readonly outcome: RoundOutcome;
  readonly winnerId: string | null;
}

export interface ViewerPlayer {
  readonly playerId: string;
  readonly score: number;
  readonly remainingTiles: readonly TileNumber[] | null;
  readonly usedTiles: readonly TileNumber[] | null;
  readonly remainingTileCount: number;
}

export interface BlackAndWhiteViewerState {
  readonly gameId: string;
  readonly hostPlayerId: string;
  readonly observerGodViewEnabled: boolean;
  readonly viewerPlayerId: string | null;
  readonly status: GameStatus;
  readonly players: readonly ViewerPlayer[];
  readonly completedRounds: readonly ViewerCompletedRound[];
  readonly currentRound: ViewerRoundInProgress | null;
  readonly winnerId: string | null;
  readonly maxRounds: number;
  readonly targetScore: number;
}
